import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShoppingBasket, RefreshCw, BookOpen, Utensils, Check, Circle } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { mondayOf, isoToday, addDays, fmtDate, num, SLOT_LABEL } from '../lib/format.js';
import { Card, Button, Sheet, Field, TextArea, Spinner, ErrorNote, Pill, PageHeader, EmptyState, TextInput } from '../components/ui.jsx';

const SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'];

// Two separate ideas, deliberately kept apart:
//
//   the MEAL   — what is planned for that slot
//   the STATUS — what actually happened: eaten, out, skipped, or nothing yet
//
// Keeping the plan after she eats out means the record says "went out instead
// of the cottage pie", which is more use later than losing what was intended.
// But the screen has to lead with what HAPPENED, not with what was planned,
// or it just looks like the app ignored her.

const OUTCOMES = {
  planned: { label: 'Not logged', tone: 'mute' },
  eaten: { label: 'Ate it', tone: 'good' },
  out: { label: 'Ate out', tone: 'accent' },
  skipped: { label: 'Skipped', tone: 'warn' },
};

export default function Nutrition() {
  const [weekStart, setWeekStart] = useState(mondayOf(isoToday()));
  const { data, loading, error, reload } = useApiData(`/nutrition/plan?week_start=${weekStart}`, [weekStart]);
  const [editing, setEditing] = useState(null);
  const [shopping, setShopping] = useState(false);
  const [busy, setBusy] = useState(false);
  const { show } = useToast();

  async function generate(regenerate = false) {
    setBusy(true);
    try {
      await api.post('/nutrition/plan', { week_start: weekStart, regenerate });
      show(regenerate ? 'New week of meals.' : 'Meal plan ready.');
      reload({ quiet: true });
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  // One tap on the circle is the whole interaction for a normal day.
  async function quickToggle(item) {
    try {
      await api.patch('/nutrition/plan', { itemId: item.id, status: item.status === 'eaten' ? 'planned' : 'eaten' });
      reload({ quiet: true });
    } catch (err) {
      show(err.message, 'crit');
    }
  }

  if (loading && !data) return <Spinner label="Loading meals" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;

  const hasPlan = data && data.plan;

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader
        title="Food"
        subtitle="Dinners work for both of you. Breakfast and lunch are yours."
        right={
          <Link to="/food/bank" className="p-2 text-textMute hover:text-accent" aria-label="Meal bank">
            <BookOpen size={20} />
          </Link>
        }
      />

      <div className="px-gutter">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 -ml-2 text-textSoft" aria-label="Previous week">
            <ChevronLeft size={20} />
          </button>
          <div className="text-center">
            <p className="text-card-title font-display">
              {weekStart === mondayOf(isoToday()) ? 'This week' : `Week of ${fmtDate(weekStart, 'short')}`}
            </p>
            <p className="text-meta text-textMute">
              {fmtDate(weekStart, 'short')} – {fmtDate(addDays(weekStart, 6), 'short')}
            </p>
          </div>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2 -mr-2 text-textSoft" aria-label="Next week">
            <ChevronRight size={20} />
          </button>
        </div>

        {!hasPlan ? (
          <Card>
            <EmptyState
              icon={Utensils}
              title="No plan for this week yet"
              body="Seven days of meals, built from your bank. Dinners will all suit both of you; every one of them can be swapped."
              action={
                <Button onClick={() => generate(false)} disabled={busy}>
                  {busy ? 'Building…' : 'Build the week'}
                </Button>
              }
            />
          </Card>
        ) : (
          <>
            <div className="flex gap-2 mb-4">
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => setShopping(true)}>
                <ShoppingBasket size={16} /> Shopping list
              </Button>
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => generate(true)} disabled={busy}>
                <RefreshCw size={16} /> Shuffle
              </Button>
            </div>

            <p className="text-meta text-textMute mb-4">
              Tap the circle when you have eaten something. Tap the meal itself to swap it, say you ate out, or mark it skipped.
            </p>

            <ul className="space-y-3 mb-6">
              {data.days.map((day) => {
                const totals = data.dayTotals.find((t) => t.date === day.date);
                const isToday = day.date === isoToday();
                return (
                  <li key={day.date}>
                    <Card className={isToday ? 'border-accent/50' : ''}>
                      <div className="flex items-baseline justify-between mb-1">
                        <h3 className="text-card-title font-display">
                          {fmtDate(day.date, 'weekday')}
                          {isToday && <span className="ml-2 text-meta text-accent font-sans">today</span>}
                        </h3>
                        {totals?.protein_g ? (
                          <span className="text-meta text-textMute">{totals.protein_g}g protein</span>
                        ) : null}
                      </div>

                      <ul className="divide-y divide-edge">
                        {SLOTS.map((slot) => (
                          <SlotRow
                            key={slot}
                            slot={slot}
                            item={day.slots[slot]}
                            onOpen={() => day.slots[slot] && setEditing(day.slots[slot])}
                            onToggle={() => day.slots[slot] && quickToggle(day.slots[slot])}
                          />
                        ))}
                      </ul>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <SlotSheet item={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); reload({ quiet: true }); }} />
      <ShoppingSheet open={shopping} onClose={() => setShopping(false)} weekStart={weekStart} />
    </div>
  );
}

function SlotRow({ slot, item, onOpen, onToggle }) {
  const status = item?.status || 'planned';
  const replaced = status === 'out' || status === 'skipped';

  // When she ate out or skipped, the outcome is the headline and the planned
  // meal drops to a footnote. Showing "Cereal" as though she ate it would be
  // plainly wrong.
  const headline = !item
    ? '—'
    : status === 'out'
      ? (item.note ? item.note : 'Ate out')
      : status === 'skipped'
        ? 'Skipped'
        : item.name;

  return (
    <li className="flex items-center gap-2">
      <button
        onClick={onOpen}
        disabled={!item}
        className="flex-1 min-w-0 py-2.5 flex items-center gap-3 text-left disabled:opacity-50"
      >
        <span className="text-label uppercase text-textMute w-16 shrink-0">{SLOT_LABEL[slot]}</span>
        <span className="flex-1 min-w-0">
          <span className={`block truncate text-body-sm ${replaced ? 'text-textSoft italic' : ''}`}>{headline}</span>
          {replaced && item.name && (
            <span className="block truncate text-meta text-textMute line-through">{item.name}</span>
          )}
        </span>
        {!replaced && item?.protein_g != null && (
          <span className="text-meta text-textMute shrink-0">{item.protein_g}g</span>
        )}
      </button>

      {item && (
        replaced ? (
          <button onClick={onOpen} className="shrink-0" aria-label={`${OUTCOMES[status].label} — change`}>
            <Pill tone={OUTCOMES[status].tone}>{OUTCOMES[status].label}</Pill>
          </button>
        ) : (
          <button
            onClick={onToggle}
            aria-pressed={status === 'eaten'}
            aria-label={status === 'eaten' ? `Ate ${item.name} — tap to undo` : `Mark ${item.name} as eaten`}
            className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
              status === 'eaten' ? 'bg-good border-good text-white' : 'border-edge text-textMute hover:border-edgeHi'
            }`}
          >
            {status === 'eaten' ? <Check size={16} /> : <Circle size={16} strokeWidth={1.5} />}
          </button>
        )
      )}
    </li>
  );
}

function SlotSheet({ item, onClose, onDone }) {
  const { show } = useToast();
  const [q, setQ] = useState('');
  const [ateNote, setAteNote] = useState('');
  const slot = item?.slot;
  const { data } = useApiData(
    slot ? `/nutrition/meals?slot=${slot}${slot === 'dinner' ? '&shared=1' : ''}` : '/nutrition/meals?slot=snack',
    [slot],
  );

  if (!item) return null;

  const meals = (data?.meals || []).filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()));
  const status = item.status || 'planned';

  async function patch(payload) {
    try {
      await api.patch('/nutrition/plan', { itemId: item.id, ...payload });
      onDone();
    } catch (err) {
      show(err.message, 'crit');
    }
  }

  return (
    <Sheet open onClose={onClose} title={`${SLOT_LABEL[slot]} — ${fmtDate(item.date, 'weekday')}`}>
      <p className="text-label uppercase text-textMute mb-2">Planned</p>
      <div className="card-flat mb-5">
        <p className="text-body font-semibold">{item.name || 'Nothing planned'}</p>
        {item.ingredients && <p className="text-body-sm text-textSoft whitespace-pre-line mt-1.5">{item.ingredients}</p>}
      </div>

      {/* What happened comes first — it is the thing she opened this for. */}
      <p className="text-label uppercase text-textMute mb-2">What happened?</p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {['eaten', 'out', 'skipped', 'planned'].map((s) => (
          <button
            key={s}
            onClick={() => patch(s === 'out' ? { status: s, note: ateNote || null } : { status: s })}
            aria-pressed={status === s}
            className={`px-3 py-2.5 rounded-control border text-body-sm font-medium transition-colors ${
              status === s ? 'bg-accent text-onAccent border-accent' : 'bg-panel text-textSoft border-edge hover:border-edgeHi'
            }`}
          >
            {s === 'planned' ? 'Not yet' : OUTCOMES[s].label}
          </button>
        ))}
      </div>

      <Field
        label="Had something else? What was it?"
        id="atenote"
        hint="Optional, and only worth it if you fancy it — but it is what makes the migraine page able to look at food properly."
      >
        <TextArea
          id="atenote"
          rows={2}
          value={ateNote || item.note || ''}
          onChange={(e) => setAteNote(e.target.value)}
          placeholder="e.g. Thai green curry and rice"
        />
      </Field>

      <div className="hr" />

      <p className="text-label uppercase text-textMute mb-2">Change what&rsquo;s planned</p>
      <TextInput placeholder="Search your meals" value={q} onChange={(e) => setQ(e.target.value)} />

      {slot === 'dinner' && (
        <p className="text-meta text-textMute mt-2 mb-1">
          Only meals that suit both of you are shown — gluten-free, lactose-free, and no lamb, pork, peppers, cucumber or coconut.
        </p>
      )}

      <ul className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
        {meals.map((m) => (
          <li key={m.id}>
            <button
              onClick={() => patch({ mealId: m.id })}
              className={`w-full text-left p-3 rounded-control border transition-colors ${
                m.id === item.meal_id ? 'border-accent bg-accentSoft' : 'border-edge hover:border-edgeHi'
              }`}
            >
              <span className="text-body-sm font-medium block">{m.name}</span>
              <span className="text-meta text-textMute">
                {[m.kcal ? `${num(m.kcal)} kcal` : null, m.protein_g ? `${m.protein_g}g protein` : null, m.prep_min ? `${m.prep_min} min` : null]
                  .filter(Boolean)
                  .join(' · ') || 'No numbers recorded'}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

function ShoppingSheet({ open, onClose, weekStart }) {
  const { data, loading } = useApiData(open ? `/nutrition/shopping?week_start=${weekStart}` : '/nutrition/shopping', [open, weekStart]);
  const [ticked, setTicked] = useState({});

  if (!open) return null;

  return (
    <Sheet open onClose={onClose} title="Shopping list">
      {loading && <p className="text-body-sm text-textMute">Working it out…</p>}
      {data && (
        <>
          <p className="text-meta text-textMute mb-3">
            From {data.mealCount} planned meals, leaving out anything you have said you are eating out for or skipping.
          </p>
          <ul className="space-y-1">
            {data.items.map((it) => (
              <li key={it.line}>
                <label className="flex items-start gap-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!ticked[it.line]}
                    onChange={() => setTicked((t) => ({ ...t, [it.line]: !t[it.line] }))}
                    className="mt-1 w-4 h-4 accent-[var(--c-accent)]"
                  />
                  <span className={`text-body-sm flex-1 ${ticked[it.line] ? 'line-through text-textMute' : ''}`}>
                    {it.line}
                    {it.times > 1 && <Pill tone="mute"> ×{it.times}</Pill>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </Sheet>
  );
}
